import { useState, useEffect, useCallback } from "react";
import { useAuth, axiosInstance } from "../App";
import { Button } from "./ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { 
  Bell, UserPlus, UserMinus, Calendar, MessageCircle, 
  Camera, Check, Loader2, X, RefreshCw
} from "lucide-react";
import { toast } from "sonner";

const SQUADLINE_LOGO = "https://customer-assets.emergentagent.com/job_c9359842-80d8-4acf-8a11-6884ae51c080/artifacts/7gldz4nd_Squadline%20logo.png";

// Icon mapping for event types
const getEventIcon = (eventType) => {
  switch (eventType) {
    case "member.joined":
      return <UserPlus className="w-4 h-4 text-green-500" />;
    case "member.left":
      return <UserMinus className="w-4 h-4 text-red-500" />;
    case "rsvp.updated":
      return <Calendar className="w-4 h-4 text-blue-500" />;
    case "message.sent":
      return <MessageCircle className="w-4 h-4 text-purple-500" />;
    case "photo.uploaded":
      return <Camera className="w-4 h-4 text-amber-500" />;
    default:
      return <Bell className="w-4 h-4 text-slate-500" />;
  }
};

export default function SquadlineNotifications({ orgId }) {
  const { token } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const fetchNotifications = useCallback(async () => {
    if (!token || !orgId) return;
    
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/squadline/notifications/${orgId}?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error("Failed to fetch Squadline notifications:", error);
    } finally {
      setLoading(false);
    }
  }, [token, orgId]);

  useEffect(() => {
    fetchNotifications();
    // Poll every 30 seconds for real-time updates
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markAsRead = async (notificationIds = null, markAll = false) => {
    try {
      await axiosInstance.post(
        `/squadline/notifications/${orgId}/mark-read`,
        { notification_ids: notificationIds, mark_all: markAll },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (markAll) {
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
        setUnreadCount(0);
        toast.success("All notifications marked as read");
      } else if (notificationIds) {
        setNotifications(prev => 
          prev.map(n => 
            notificationIds.includes(n.notification_id) ? { ...n, read: true } : n
          )
        );
        setUnreadCount(prev => Math.max(0, prev - notificationIds.length));
      }
    } catch (error) {
      console.error("Failed to mark notifications as read:", error);
      toast.error("Failed to update notifications");
    }
  };

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (!orgId) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative"
          data-testid="squadline-notifications-btn"
        >
          <div className="w-6 h-6 bg-white border border-slate-200 rounded flex items-center justify-center shadow-sm">
            <img 
              src={SQUADLINE_LOGO} 
              alt="Squadline" 
              className="h-4 w-4 object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = '<span class="text-black text-sm font-bold">S</span>';
              }}
            />
          </div>
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between p-3 border-b bg-gradient-to-r from-amber-500 to-orange-500">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-white rounded flex items-center justify-center">
              <img 
                src={SQUADLINE_LOGO} 
                alt="Squadline" 
                className="h-4 w-4 object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = '<span class="text-black text-sm font-bold">S</span>';
                }}
              />
            </div>
            <span className="font-semibold text-white">Squadline Updates</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-white hover:bg-white/20"
            onClick={fetchNotifications}
            disabled={loading}
          >
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        <div className="max-h-80 overflow-y-auto">
          {loading && notifications.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-8 px-4">
              <Bell className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">No notifications yet</p>
              <p className="text-xs text-slate-400 mt-1">
                Updates from your Squadline teams will appear here
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((notification) => (
                <div
                  key={notification.notification_id}
                  className={`p-3 hover:bg-slate-50 transition-colors ${
                    !notification.read ? 'bg-amber-50/50' : ''
                  }`}
                >
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      {getEventIcon(notification.event_type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${!notification.read ? 'font-medium' : ''} text-slate-900`}>
                        {notification.title}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                        {notification.message}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        {formatTimeAgo(notification.created_at)}
                      </p>
                    </div>
                    {!notification.read && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 flex-shrink-0"
                        onClick={() => markAsRead([notification.notification_id])}
                      >
                        <Check className="w-3 h-3 text-slate-400" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {notifications.length > 0 && unreadCount > 0 && (
          <div className="p-2 border-t bg-slate-50">
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-amber-600 hover:text-amber-700 hover:bg-amber-50"
              onClick={() => markAsRead(null, true)}
            >
              <Check className="w-3 h-3 mr-1" />
              Mark all as read
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
